/// <amd-module name="Wit/WorkItemForm/Links/Controls/ArtifactLink" />
import * as React from "react";
import { useCallback } from "react";
import { IVssLayoutManager, useService } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import { ILinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { LinkTypeUpdateDate, LinkTypeState, LinkTypeAction, LinkTypeSubtitle } from "Wit/WorkItemLinksRegistration/Components";
import { RegisteredLinkTypeNames } from "Wit/WorkItemLinksRegistration/Constants";
import { ArtifactLinkIcon } from "./ArtifactLinkIcon";

export interface IArtifactLinkProps {
    artifactData: ILinkDisplayData;
    showDetails?: boolean;
    className?: string;
    onDismissWorkItemForm?: (id?: number) => void;
}

export function ArtifactLink({ artifactData, showDetails, className, onDismissWorkItemForm }: IArtifactLinkProps) {
    const layoutManager = useService<IVssLayoutManager>("IVssLayoutManager");

    const onLinkClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>, artifactData: ILinkDisplayData) => {
            const isWorkItemLink = artifactData.registeredLinkType === RegisteredLinkTypeNames.Related;
            const workItemId = artifactData.id;
            if (!isWorkItemLink || typeof workItemId !== "number" || event.ctrlKey || event.metaKey) {
                // Do nothing and let the click event handle as normal (i.e. navigate to the href)
                return;
            }

            event.stopPropagation();
            event.preventDefault();

            layoutManager.renderCallout(onDismiss => (
                <WorkItemFormDialog
                    workItemId={workItemId}
                    onDismiss={onDismiss}
                    onSaveComplete={() => {
                        onDismissWorkItemForm?.(typeof artifactData.id === "number" ? artifactData.id : undefined);
                    }}
                />
            ));
        },
        [layoutManager]
    );

    return (
        <div className={`artifact-link-container white-space-nowrap ${className}`}>
            <div className="flex-row flex-center artifact-link">
                {/* Note: usually using an index as a key is not safe, but since this array should never be reordered it's actually fine. */}
                {artifactData.icons.map((icon, i) => (
                    <ArtifactLinkIcon key={"" + i} iconDescriptor={icon} />
                ))}

                {artifactData.displayId && artifactData.displayId.length > 0 && (<div className="artifact-link-id secondary-text margin-right-4">{artifactData.displayId}</div>)}

                {artifactData.uri === "" || artifactData.uri === "#" ? (
                    <span className="artifact-link-link h-scroll-hidden text-ellipsis">{artifactData.title}</span>
                ) : (
                    <Link
                        className="artifact-link-link h-scroll-hidden text-ellipsis"
                        href={artifactData.uri}
                        onClick={e => onLinkClick(e, artifactData)}
                        target={artifactData.registeredLinkType !== RegisteredLinkTypeNames.Related ? "_blank" : undefined}
                        removeUnderline={true}
                    >
                        {artifactData.title}
                    </Link>
                )}
                <LinkTypeSubtitle link={artifactData} />
            </div>
            {showDetails && (
                <>
                    <div className="artifact-details margin-left-16 flex-row flex-center">
                        <span className="padding-right-8 text-ellipsis">
                            <LinkTypeUpdateDate link={artifactData} />
                        </span>
                        <span className="text-ellipsis">
                            <LinkTypeState link={artifactData} />
                        </span>
                    </div>
                    <div className="artifact-details margin-left-16 flex-row flex-center">
                        <span className="text-ellipsis">
                            <LinkTypeAction link={artifactData} />
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
